import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowUpRight, CheckCircle2, Hourglass, Inbox as InboxIcon, TrendingDown, TrendingUp, } from 'lucide-react';
import { countCriticalAlerts } from '@/utils/escalation';
import { cn } from '@/lib/utils';
function trendChip(kind, value) {
    const Icon = kind === 'down' ? TrendingDown : TrendingUp;
    const tone = kind === 'down'
        ? 'bg-emerald-50 text-emerald-700 ring-emerald-200/70'
        : kind === 'up'
            ? 'bg-rose-50 text-rose-700 ring-rose-200/70'
            : 'bg-muted text-muted-foreground ring-border/70';
    return (_jsxs("span", { className: cn('inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium ring-1', tone), children: [_jsx(Icon, { className: "h-3 w-3", "aria-hidden": true }), value] }));
}
export function StatSummaryCards({ communications }) {
    const navigate = useNavigate();
    const total = communications.length;
    const pending = communications.filter((c) => c.validationStatus === 'pending').length;
    const validated = communications.filter((c) => c.validationStatus === 'validated').length;
    const critical = countCriticalAlerts(communications);
    const items = [
        {
            key: 'total',
            label: 'Total communications',
            value: total,
            hint: 'In current snapshot',
            icon: InboxIcon,
            iconTint: 'bg-primary/10 text-primary',
            trend: { value: '+12% wk', kind: 'up' },
            to: '/inbox',
        },
        {
            key: 'pending',
            label: 'Pending review',
            value: pending,
            hint: 'Awaiting analyst action',
            icon: Hourglass,
            iconTint: 'bg-amber-100 text-amber-700',
            trend: { value: '-4% wk', kind: 'down' },
            to: '/inbox?validation=pending',
        },
        {
            key: 'validated',
            label: 'Validated',
            value: validated,
            hint: 'Confirmed by humans',
            icon: CheckCircle2,
            iconTint: 'bg-emerald-100 text-emerald-700',
            trend: { value: '+8% wk', kind: 'up' },
            to: '/inbox?validation=validated',
        },
        {
            key: 'critical',
            label: 'Critical alerts',
            value: critical,
            hint: critical > 0 ? 'Requires immediate attention' : 'All clear',
            icon: AlertTriangle,
            iconTint: 'bg-rose-100 text-rose-700',
            trend: {
                value: critical > 0 ? 'Active' : 'Stable',
                kind: critical > 0 ? 'up' : 'flat',
            },
            to: '/inbox?alert=critical',
        },
    ];
    return (_jsx("div", { className: "grid gap-4 sm:grid-cols-2 xl:grid-cols-4", children: items.map((item, i) => {
            const Icon = item.icon;
            return (_jsxs(motion.button, { type: "button", onClick: () => navigate(item.to), initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.04 }, title: `Open inbox — ${item.label.toLowerCase()}`, className: "group relative cursor-pointer overflow-hidden rounded-2xl border border-border/60 bg-card p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40", children: [_jsxs("div", { className: "flex items-start justify-between", children: [_jsx("div", { className: cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ring-border/40', item.iconTint), "aria-hidden": true, children: _jsx(Icon, { className: "h-5 w-5" }) }), trendChip(item.trend.kind, item.trend.value)] }), _jsx("p", { className: "mt-5 text-sm font-medium text-muted-foreground", children: item.label }), _jsx("p", { className: "mt-1 text-3xl font-bold tabular-nums tracking-tight text-foreground", children: item.value }), _jsx("p", { className: "mt-1 text-xs text-muted-foreground", children: item.hint }), _jsx("span", { "aria-hidden": true, className: "absolute bottom-4 right-4 flex h-6 w-6 items-center justify-center rounded-md bg-muted text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100", children: _jsx(ArrowUpRight, { className: "h-3.5 w-3.5" }) })] }, item.key));
        }) }));
}
